import { useMemo } from "react";

const DAYS = [
  { key: "mon", label: "Lundi" },
  { key: "tue", label: "Mardi" },
  { key: "wed", label: "Mercredi" },
  { key: "thu", label: "Jeudi" },
  { key: "fri", label: "Vendredi" },
  { key: "sat", label: "Samedi" },
  { key: "sun", label: "Dimanche" },
];

const TIME_REGEX = /^\d{2}:\d{2}$/;

type OpeningHours = Record<string, [string, string][]>;

/**
 * Erreurs de validation dérivées de la valeur (clé `${jour}-${index}`).
 * Pure : utilisable pour l'affichage ET pour bloquer l'enregistrement côté parent.
 */
export function openingHoursErrors(value: OpeningHours): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const { key } of DAYS) {
    const ranges = value?.[key] ?? [];
    ranges.forEach(([open, close], index) => {
      const errorKey = `${key}-${index}`;
      if (!TIME_REGEX.test(open) || !TIME_REGEX.test(close)) {
        errors[errorKey] = "Format invalide (HH:MM)";
      } else if (open >= close) {
        errors[errorKey] = "L'heure de fermeture doit être après l'ouverture";
      }
    });
  }
  return errors;
}

interface OpeningHoursEditorProps {
  value: OpeningHours;
  onChange: (value: OpeningHours) => void;
}

export function OpeningHoursEditor({ value, onChange }: OpeningHoursEditorProps) {
  const errors = useMemo(() => openingHoursErrors(value), [value]);

  const updateDay = (dayKey: string, ranges: [string, string][]) => {
    onChange({ ...value, [dayKey]: ranges });
  };

  const addRange = (dayKey: string) => {
    const current = value[dayKey] ?? [];
    updateDay(dayKey, [...current, ["09:00", "10:00"]]);
  };

  const removeRange = (dayKey: string, index: number) => {
    const current = value[dayKey] ?? [];
    updateDay(dayKey, current.filter((_, i) => i !== index));
  };

  const updateRange = (dayKey: string, index: number, field: "open" | "close", time: string) => {
    const current = value[dayKey] ?? [];
    const newRanges = [...current];
    const range = newRanges[index];
    if (!range) return;

    if (field === "open") {
      newRanges[index] = [time, range[1]];
    } else {
      newRanges[index] = [range[0], time];
    }
    updateDay(dayKey, newRanges);
  };

  return (
    <div className="space-y-4">
      {DAYS.map(({ key, label }) => {
        const ranges = value[key] ?? [];
        const isClosed = ranges.length === 0;

        return (
          <div key={key} className="rounded border border-ink-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="font-semibold text-ink-700">{label}</label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!isClosed}
                  onChange={(e) => {
                    if (e.target.checked && isClosed) {
                      // Open for first time
                      updateDay(key, [["09:00", "12:00"]]);
                    } else if (!e.target.checked && !isClosed) {
                      // Close
                      updateDay(key, []);
                    }
                  }}
                  className="cursor-pointer"
                />
                <span className="text-xs text-ink-500">Ouvert</span>
              </label>
            </div>

            {isClosed ? (
              <p className="text-sm text-ink-400 italic">Fermé</p>
            ) : (
              <div className="space-y-3">
                {ranges.map((range, idx) => {
                  const errorKey = `${key}-${idx}`;
                  const hasError = errorKey in errors;

                  return (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2">
                          <input
                            type="time"
                            value={range[0]}
                            onChange={(e) => updateRange(key, idx, "open", e.target.value)}
                            className="input flex-1"
                          />
                          <span className="flex items-center text-ink-400">–</span>
                          <input
                            type="time"
                            value={range[1]}
                            onChange={(e) => updateRange(key, idx, "close", e.target.value)}
                            className="input flex-1"
                          />
                        </div>
                        {hasError && (
                          <p className="text-xs text-brand-500">{errors[errorKey]}</p>
                        )}
                      </div>
                      {ranges.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRange(key, idx)}
                          className="btn-ghost px-2 py-2 text-brand-500 hover:bg-brand-50 mt-1"
                          title="Supprimer cette plage"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={() => addRange(key)}
                  className="btn-outline text-xs py-1.5 px-2 w-full"
                >
                  + Ajouter une plage
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
